import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const container = {
  initial: {},
  animate: { transition: { staggerChildren: 0.06 } },
};

const fadeUp = {
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.25, 0.1, 0.25, 1] } },
};

const worksList = [
  { title: "Knell", year: "2026", slug: "knell" },
  { title: "Ocean", year: "2026", slug: "ocean" },
  { title: "In Quarters", year: "2026", slug: "in-quarters" },
  { title: "Tie & Untie", year: "2025", slug: "tie-and-untie" },
  { title: "Surveillance", year: "2025", slug: "surveillance" },
  { title: "Wait", year: "2025", slug: "wait" },
  { title: "Stagnate", year: "2025", slug: "stagnate" },
  { title: "Poyraz", year: "2025", slug: "poyraz" },
  { title: "Yakamoz", year: "2024", slug: "yakamoz" },
  { title: "Clarity", year: "2024", slug: "clarity" },
  { title: "Surrounded", year: "2024", slug: "surrounded" },
  { title: "Sparrow", year: "2023", slug: "sparrow" },
  { title: "Antony & Cleopatra", year: "2022", slug: "antony-and-cleopatra" },
];

function SectionHeading({ children }) {
  return (
    <h3 className="font-sans text-[11px] font-semibold tracking-[0.06em] uppercase text-[var(--faint)] mb-5">
      {children}
    </h3>
  );
}

function Row({ left, right }) {
  return (
    <div className="flex justify-between items-baseline gap-4 py-[6px]">
      <span className="font-sans text-[14px] text-[var(--body)]">{left}</span>
      <span className="font-mono text-[12px] text-[var(--faint)] tabular-nums flex-shrink-0">
        {right}
      </span>
    </div>
  );
}

export default function About() {
  return (
    <motion.section
      className="max-w-[900px] mx-auto px-6 sm:px-8 py-20 sm:py-28"
      variants={container}
      initial="initial"
      animate="animate"
    >
      {/* Photo left, bio right */}
      <motion.div
        variants={fadeUp}
        className="grid grid-cols-1 sm:grid-cols-[240px_1fr] gap-8 sm:gap-14 items-start"
      >
        <figure className="relative overflow-hidden w-full sm:w-[240px] aspect-[3/4]">
          <img
            src="/photos/img_5208_crop.jpeg"
            alt="Murat Gulcelik"
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
          />
        </figure>

        <div className="space-y-5 font-sans text-[14px] leading-[1.8] text-[var(--body)]">
          <p>
            I'm a composer writing across chamber music, electronics, and ambient, often
            with sound design in the mix. My work moves between scored pieces for acoustic
            players, modular and digital synthesis, and music written for dance and film.
          </p>
          <p>
            Outside of composing, I'm working on a few projects — audio plugins and a
            video game — and I play various instruments, playing bass for{" "}
            <a
              href="https://www.thefleok.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[var(--text)] underline underline-offset-[3px] decoration-[var(--rule)] hover:decoration-[var(--text)] transition-colors"
            >
              The Fleok
            </a>
            .
          </p>
        </div>
      </motion.div>

      {/* Works left column, Recognition/Education/Tools right column */}
      <motion.div
        variants={fadeUp}
        className="mt-20 grid grid-cols-1 sm:grid-cols-2 gap-14 sm:gap-20"
      >
        <div>
          <SectionHeading>Works</SectionHeading>
          <div>
            {worksList.map((w) => (
              <Link
                key={w.slug}
                to={`/work/${w.slug}`}
                className="group flex justify-between items-baseline gap-4 py-[6px] border-b border-transparent hover:border-[var(--rule)] transition-colors"
              >
                <span className="font-sans text-[14px] text-[var(--body)] group-hover:text-[var(--text)] transition-colors">
                  {w.title}
                </span>
                <span className="font-mono text-[12px] text-[var(--faint)] tabular-nums flex-shrink-0">
                  {w.year}
                </span>
              </Link>
            ))}
          </div>
        </div>

        <div className="space-y-14">
          <div>
            <SectionHeading>Recognition</SectionHeading>
            <Row left="OVO Music — Selected Composer" right="2026" />
            <Row left="Charles S. Miller Prize, Columbia" right="2025" />
            <Row left="Douglas Moore Prize, Columbia" right="2024" />
          </div>

          <div>
            <SectionHeading>Education</SectionHeading>
            <Row left="Columbia University — CS & Music" right="2025" />
            <Row left="Juilliard Pre-College — Double Bass" right="2021" />
          </div>

          <div>
            <SectionHeading>Tools</SectionHeading>
            <div className="space-y-1 font-sans text-[13px] leading-[1.7] text-[var(--muted)]">
              <p>Double Bass, Electric Bass, Guitar, Piano, Violin</p>
              <p>Ableton, Logic, Pro Tools, Dorico, Max/MSP, VCV Rack</p>
              <p>Python, Sonic Pi, TouchDesigner</p>
            </div>
          </div>

          <a
            href="/resume.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block font-sans text-[12px] text-[var(--muted)] hover:text-[var(--text)] transition-colors underline underline-offset-[3px] decoration-[var(--rule)]"
          >
            Download Resume
          </a>
        </div>
      </motion.div>
    </motion.section>
  );
}
